import { useQuery } from '@tanstack/react-query-v4'
import { QueryKey } from '@api'
import { readContract } from '@services/shared/contract.read.service'
import { marketplaceListingsContract } from '@contracts/marketplace'
import { getAddressesConfig, nftEffectiveTokenValueBatch } from '@contracts'
import type { HomePageFundRecord } from '@domain/landing/homePageFundPayload'
import type { StrapiFundData } from '@types'
import { Address } from 'viem'
import { useMemo } from 'react'

type MarketplaceCollectionListing = {
  listed: boolean
  sold: boolean
  seller: Address
  nftContract: Address
  tokenId: bigint
  price: bigint
  listingId: bigint
}

export type PreIpoLowestInAppValuations = Record<string, number>

type FundValuationInput = {
  fundUrlId: string
  investmentAddress: Address
  referenceValuation: number
}

type FundValuationSource = Pick<StrapiFundData, 'fundUrlId' | 'referenceValue'> & {
  marketplace?: { investmentAddress?: string }
}

function getFundValuationInputs(funds: FundValuationSource[]): FundValuationInput[] {
  return funds.reduce<FundValuationInput[]>((acc, fund) => {
    const investmentAddress = fund.marketplace?.investmentAddress
    const referenceValuation = Number(fund.referenceValue ?? 0)
    if (
      !investmentAddress ||
      !Number.isFinite(referenceValuation) ||
      referenceValuation <= 0
    ) {
      return acc
    }
    acc.push({
      fundUrlId: fund.fundUrlId,
      investmentAddress: investmentAddress as Address,
      referenceValuation,
    })
    return acc
  }, [])
}

function calculateLowestInAppValuation(
  fundEntryValuation: number,
  listings: MarketplaceCollectionListing[],
  entryPrices: readonly bigint[],
): number | null {
  const valuations: number[] = []

  listings.forEach((listing, index) => {
    const entryPrice = entryPrices[index]
    if (!entryPrice || entryPrice <= 0n || listing.price <= 0n) return

    const tokenCurrentPrice = Number(listing.price)
    const tokenEntryPrice = Number(entryPrice)

    if (tokenCurrentPrice <= 0 || tokenEntryPrice <= 0) return

    valuations.push(fundEntryValuation * (tokenCurrentPrice / tokenEntryPrice))
  })

  if (valuations.length === 0) return null

  return Math.min(...valuations)
}

async function fetchLowestInAppValuations(
  fundInputs: FundValuationInput[],
): Promise<PreIpoLowestInAppValuations> {
  if (fundInputs.length === 0) return {}

  const addresses = getAddressesConfig() as { Marketplace: Address }
  const result: PreIpoLowestInAppValuations = {}

  await Promise.all(
    fundInputs.map(async ({ fundUrlId, investmentAddress, referenceValuation }) => {
      try {
        const listings = await readContract<MarketplaceCollectionListing[]>({
          ...marketplaceListingsContract,
          address: addresses.Marketplace,
          functionName: 'getAllActiveCollectionListings',
          args: [investmentAddress],
        })

        const activeListings = (listings ?? []).filter((l) => l.listed && !l.sold)
        if (activeListings.length === 0) return

        const tokenIds = activeListings.map((l) => l.tokenId)
        const entryPrices = await nftEffectiveTokenValueBatch(investmentAddress, tokenIds)

        if (!entryPrices) return

        const lowest = calculateLowestInAppValuation(
          referenceValuation,
          activeListings,
          entryPrices,
        )
        if (lowest === null) return

        result[fundUrlId] = lowest
      } catch (error) {
        console.error('Failed to calculate pre-IPO in-app valuation', error)
      }
    }),
  )

  return result
}

export function usePreIpoLowestInAppValuations(
  funds: FundValuationSource[] | HomePageFundRecord[],
) {
  const fundInputs = useMemo(() => getFundValuationInputs(funds), [funds])

  const queryKey = useMemo(
    () =>
      fundInputs.map((f) => [
        f.fundUrlId,
        f.investmentAddress.toLowerCase(),
        f.referenceValuation,
      ]),
    [fundInputs],
  )

  return useQuery<PreIpoLowestInAppValuations>({
    queryKey: [QueryKey.PreIpoLowestInAppValuations, queryKey],
    queryFn: () => fetchLowestInAppValuations(fundInputs),
    enabled: fundInputs.length > 0,
    staleTime: 60_000,
  })
}
